<template>
	<Panel title="Lease Summary">
		<div class="numbers-summary">
			<div class="summary-table">
				<div class="header-row six-cols">
					<span>Description</span>
					<span class="text-right">Monthly Payment</span>
					<span class="text-right">Term</span>
					<span class="text-right">Buy {{ isMoneyFactorAsRate ? 'Rate' : 'MF' }}</span>
					<span class="text-right">Sell {{ isMoneyFactorAsRate ? 'Rate' : 'MF' }}</span>
					<span class="text-right">Next Payment Date</span>
				</div>
				<div class="data-row six-cols">
					<span>Lease Information</span>
					<span class="text-right">{{ monthlyPayment }}</span>
					<span class="text-right">{{ leaseTerm }}</span>
					<span class="text-right">{{ buyRate }}</span>
					<span class="text-right">{{ sellRate }}</span>
					<span class="text-right">{{ nextPaymentDate }}</span>
				</div>
			</div>
		</div>
	</Panel>
</template>

<script setup lang="ts">
	import Panel from '@core/components/Panel.vue';
	import { computed, inject } from 'vue';
	import FIMenu from '@core/classes/FIMenu';
	import financials from '@core/services/financials';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';

	const $global = inject<SaturnGlobalPlugin>('$global');

	export interface Props {
		data: FIMenu;
	}

	const props = defineProps<Props>();

	const nextPaymentDate = computed(() => {
		const today = new Date();
		const paymentDate = new Date(today);
		paymentDate.setMonth(today.getMonth() + 1);
		return paymentDate.toLocaleDateString();
	});

	const monthlyPayment = computed(() => {
		if (!props.data) return '-';
		return props.data.getCurrentBuyersOrder().finalNumbers.monthlyPaymentWithTax?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) ?? '-';
	});

	const leaseTerm = computed(() => {
		if (!props.data) return '';
		return props.data.leaseTerms?.getAcceptedTerm()?.term || '';
	});

	const selectedLender = computed(() => {
		if (props.data.lender.lenderCode) {
			return $global.Lenders.find(l => l.lenderCode == props.data.lender.lenderCode);
		}
		return null;
	});

	const isMoneyFactorAsRate = computed(() => selectedLender.value?.versions[0].lease.isMoneyFactorAsRate ?? false);

	const buyRate = computed(() => {
		if (!props.data) return '-';
		const term = props.data.leaseTerms?.getAcceptedTerm();
		if (!term) return '-';

		if (isMoneyFactorAsRate.value) {
			return term.buyrate?.toFixed(2) + '%';
		} else {
			return term.buyRateMoneyFactor?.toFixed(5);
		}
	});

	const sellRate = computed(() => {
		if (!props.data) return '-';
		const term = props.data.leaseTerms?.getAcceptedTerm();
		if (!term) return '-';

		if (isMoneyFactorAsRate.value) {
			return term.sellrate?.toFixed(2) + '%';
		} else {
			return term.moneyfactor?.toFixed(5);
		}
	});
</script>

<style scoped>
	.numbers-summary {
		padding: 1rem;
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
	}

	.summary-table {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.header-row.six-cols,
	.data-row.six-cols {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
		align-items: center;
		font-size: 1rem;
	}

	.header-row {
		font-weight: 700;
		border-bottom: 1px solid var(--border-color, #ccc);
		padding-bottom: 0.25rem;
	}

	.data-row {
		color: var(--text-color);
		min-height: 2.2rem;
	}

	.text-right {
		text-align: right;
	}

	.displaypricebold {
		color: var(--text-color);
	}
</style>
